import React from "react";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import { RootState } from "../../app/store";
// ROLE MARKERS — rendered, not described, so the legend cannot drift from
// the tables and pickers it explains.
import MasterRoleStar from "./MasterRoleStar";
import TenantRoleShield from "./TenantRoleShield";

interface RoleMarkerLegendProps {
  /** Heading above the rows; pass "" for none. */
  title?: string;
}

/**
 * Legend for the markers that sit beside every role name.
 *
 * The star and the shield appear in the Roles table, in both "Role" pickers
 * and in the Add User modal, and nothing anywhere said what they meant — an
 * operator had to infer it from which roles happened to carry which.
 *
 * The icons here are the SAME components those call sites render, so a change
 * to MasterRoleStar or TenantRoleShield reaches this legend automatically. A
 * legend that hardcoded its own <FontAwesomeIcon> would quietly start
 * describing the wrong picture the first time either marker was restyled.
 */
function RoleMarkerLegend({ title = "What the role icons mean" }: RoleMarkerLegendProps) {
  const { theme } = useSelector((state: RootState) => state.home);

  return (
    <div
      className={`drawer__box__${theme}`}
      style={{ padding: "16px" }}
      data-testid="role-marker-legend"
    >
      {title !== "" && <h6 className="generalStyles__mrFix">{title}</h6>}
      <div className="container">
        <div className="row" style={{ marginTop: 8 }}>
          <div className="col-md-3">
            <MasterRoleStar show />
            <span className="drawer__text">LiftNet role</span>
          </div>
          <div className="col-md-9">
            <span className="drawer__text">
              Defined by LiftNet in the master organization and shared across every tenant. Editing
              or deleting one is not offered here — it is not this organization&apos;s to change.
            </span>
          </div>
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <div className="col-md-3">
            <TenantRoleShield show />
            <span className="drawer__text">Organization role</span>
          </div>
          <div className="col-md-9">
            <span className="drawer__text">
              Belongs to this organization. It can be edited, duplicated and deleted, and its
              permissions apply only to people in this organization.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleMarkerLegend;
